<template>
  <v-dialog v-model="dialogModel" max-width="800" persistent>
    <v-card class="pa-4">
      <v-card-title>Add New Event</v-card-title>

      <v-card-text>
        <div class="d-flex flex-column ga-5">
          <span class="error-message" v-for="e in errors" :key="e">{{
            e
          }}</span>

          <!-- Form Fields -->
          <template v-for="field in formFields" :key="field.id">
            <div
              :class="[
                field.type === 'autocomplete' ? 'filed-autocomplete' : 'field',
                'd-flex flex-column ga-2',
              ]"
            >
              <label
                v-if="field.type !== 'switch'"
                :ref="field.ref"
                :for="field.id"
              >
                {{ field.label }}
                <span v-if="field.required" class="required">*</span>
              </label>

              <!-- Regular Input -->
              <input
                v-if="field.type === 'input'"
                :id="field.id"
                v-model="formData[field.id]"
                :type="field.inputType || 'text'"
                @input="updateValidationClass(field.ref, formData[field.id])"
              />

              <!-- Date Input -->
              <input
                v-else-if="field.type === 'date'"
                :id="field.id"
                v-model="formData[field.id]"
                type="text"
                :ref="field.id"
                @input="updateValidationClass(field.ref, formData[field.id])"
              />

              <!-- Autocomplete -->
              <v-autocomplete
                v-else-if="field.type === 'autocomplete'"
                v-model="formData[field.id]"
                :items="field.items"
                :multiple="field.multiple"
                :item-value="field.itemValue"
                :item-title="field.itemTitle"
                hide-details
                @change="updateValidationClass(field.ref, formData[field.id])"
              />

              <!-- Switch -->
              <div
                v-else-if="field.type === 'switch'"
                class="d-flex justify-end align-center ga-2 custom-switch"
              >
                <label :for="field.id">{{ field.label }}</label>
                <v-switch
                  color="green"
                  v-model="formData[field.id]"
                  hide-details
                  inset
                  :true-value="1"
                  :false-value="0"
                ></v-switch>
              </div>
            </div>
          </template>
        </div>
      </v-card-text>

      <v-card-actions class="sticky-footer-dialog">
        <v-spacer></v-spacer>
        <span class="cancel-btn cursor-pointer" @click="cancelDialog"
          >Cancel</span
        >
        <span class="choose-btn cursor-pointer" @click="saveEvent">Save</span>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";
import { getFieldError } from "@/utils/formValdiation.js";
import apiClient from "@/core/buildRequest";
import { useContactsStore } from "@/store/crm/useContactsStore";
import { useAccountsStore } from "@/store/crm/useAccountsStore";
import { useProjectsStore } from "@/store/crm/useProjectsStore";
import { useDealsStore } from "@/store/crm/useDealsStore";
import { useTasksStore } from "@/store/crm/useTasksStore";
import { useEventsStore } from "@/store/crm/useEventsStore";
import { useGeneralStore } from "@/store/General";
export default {
  name: "EventDialog",

  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    contacts: {
      type: Array,
      default: () => [],
    },
    accounts: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      selectcontacts: "",
      selectaccounts: "",
      selectprojects: "",
      selectDeals: "",
      selectTasks: "",
      selectagents: "",
      formData: {
        name: "",
        objectives: "",
        date: "",
        add_to_calendar: 1,
        assigness: [],
        status: "",
        type: "",
        contact_id: [],
        account_id: [],
        project_id: "",
        deal_id: "",
      },
      errors: [],
      fieldErrors: {},
      flatpickrInstances: {},
      contacts_store: useContactsStore(),
      accounts_store: useAccountsStore(),
      projects_store: useProjectsStore(),
      deals_store: useDealsStore(),
      tasks_store: useTasksStore(),
      event_store: useEventsStore(),
      genral_store: useGeneralStore(),
    };
  },

  computed: {
    dialogModel: {
      get() {
        return this.dialog;
      },
      set(value) {
        this.$emit("update:dialog", value);
      },
    },

    formFields() {
      return [
        {
          id: "name",
          label: "Event Name",
          type: "input",
          required: true,
          ref: "eventNameInput",
        },
        {
          id: "objectives",
          label: "Objectives",
          type: "input",
          required: true,
          ref: "objectivesInput",
        },
        {
          id: "date",
          label: "Event Date",
          type: "date",
          required: true,
          ref: "eventDateInput",
        },
        {
          id: "add_to_calendar",
          label: "Add To Calendar",
          type: "switch",
          required: false,
        },
        {
          id: "member_id",
          label: "Assignees",
          type: "autocomplete",
          items: this.selectagents,
          multiple: true,
          required: true,
          ref: "assigneesInput",
          itemTitle: "name",
          itemValue: "id",
        },
        {
          id: "status",
          label: "Status",
          type: "autocomplete",
          items: ["active", "pending", "on_hold", "completed", "closed"],
          required: true,
          ref: "statusInput",
        },
        {
          id: "type",
          label: "Event Type",
          type: "autocomplete",
          items: ["phone_call", "f2f_meeting", "virtual_meeting"],
          required: true,
          ref: "eventTypeInput",
        },
        {
          id: "contact_id",
          label: "Contacts",
          type: "autocomplete",
          items: this.selectcontacts,
          multiple: true,
          required: true,
          ref: "contactsInput",
          itemTitle: "contact_number",
          itemValue: "id",
        },
        {
          id: "account_id",
          label: "Account",
          type: "autocomplete",
          items: this.selectaccounts,
          multiple: false,
          required: true,
          ref: "accountsInput",
          itemTitle: "account_number",
          itemValue: "id",
        },
        {
          id: "project_id",
          label: "Project",
          type: "autocomplete",
          items: this.selectprojects,
          required: true,
          ref: "projectInput",
          itemTitle: "project_number",
          itemValue: "id",
        },
        {
          id: "deal_id",
          label: "Deal",
          type: "autocomplete",
          items: this.selectDeals,
          required: true,
          ref: "dealInput",
          itemTitle: "deal_number",
          itemValue: "id",
        },
        {
          id: "task_id",
          label: "Task",
          type: "autocomplete",
          items: this.selectTasks,
          required: true,
          ref: "taskInput",
          itemTitle: "task_number",
          itemValue: "id",
        },
      ];
    },
    all_contacts() {
      return this.contacts_store?.getContacts || [];
    },
    all_accounts() {
      return this.accounts_store?.getAccounts || [];
    },
    all_projects() {
      return this.projects_store?.getProjects || [];
    },
    all_deals() {
      return this.deals_store?.getDeals || [];
    },
    all_tasks() {
      return this.tasks_store?.getTasks || [];
    },
  },

  watch: {
    dialog(newVal) {
      if (newVal) {
        this.getAllcontacts();
        this.getAllAccounts();
        this.getAllprojects();
        this.getAllDeals();
        this.getAllTasks();
        this.getAllagents();
        this.$nextTick(() => {
          this.initializeFlatPickr();
        });
      } else {
        this.destroyFlatPickr();
      }
    },

    contacts: {
      handler(newVal) {
        this.formData.contact_id = newVal;
      },
      deep: true,
    },

    accounts: {
      handler(newVal) {
        this.formData.accounts = newVal;
      },
      deep: true,
    },
  },

  beforeUnmount() {
    this.destroyFlatPickr();
  },

  methods: {
    initializeFlatPickr() {
      this.destroyFlatPickr();

      const eventDateEl = this.$refs.date?.[0];
      if (eventDateEl) {
        this.flatpickrInstances.eventDate = flatpickr(eventDateEl, {
          enableTime: false,
          dateFormat: "Y-m-d",
          onChange: (selectedDates) => {
            this.formData.date = selectedDates[0]
              ? selectedDates[0].toISOString().split("T")[0]
              : "";
            this.updateValidationClass("eventDateInput", this.formData.date);
          },
        });
      }
    },

    destroyFlatPickr() {
      Object.values(this.flatpickrInstances).forEach((instance) => {
        if (instance) {
          instance.destroy();
        }
      });
      this.flatpickrInstances = {};
    },

    validateForm() {
      this.errors = [];
      this.fieldErrors = {};
      let isValid = true;

      for (const field of this.formFields) {
        if (!field.required) continue;

        const value = this.formData[field.id];
        const error = getFieldError(field.label, value);

        if (error) {
          isValid = false;
          this.fieldErrors[field.id] = error;
          let el = this.$refs[field.ref]?.[0];
          if (el) {
            el.classList.add("error-validation");
          }
        }
      }

      return isValid;
    },

    updateValidationClass(ref, value) {
      const el = this.$refs[ref]?.[0];
      if (el) {
        el.classList[!value ? "add" : "remove"]("error-validation");
      }
    },

    resetForm() {
      Object.keys(this.formData).forEach((key) => {
        this.formData[key] = Array.isArray(this.formData[key]) ? [] : "";
      });
      this.formData.add_to_calendar = 1;
      this.errors = [];
      this.fieldErrors = {};
    },

    cancelDialog() {
      this.resetForm();
      this.$emit("update:dialog", false);
    },

    async saveEvent() {
      if (!this.validateForm()) return;
      this.genral_store.ShowLoading();
      try {
        await apiClient.post("/leads-managment/events", this.formData);
        this.resetForm();
        this.$emit("update:dialog", false);
        this.event_store.getAllEvents();
        this.genral_store.showSnackbar("Event saved successfully", "#237276");
      } catch (error) {
        this.errors.push(error.response?.data?.msg || "An error occurred");
        console.error(error);
      } finally {
        this.genral_store.HideLoading();
      }
    },
    //get Allagents
    async getAllagents() {
      try {
        var response = await apiClient.get(`/leads-managment/all-agents`);
        const agentid = response.data.data;
        this.selectagents = Array.isArray(agentid)
          ? agentid.map((e) => ({ id: e.id, name: e.first_name + e.last_name }))
          : [];
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
    //get Allcontacts
    async getAllcontacts() {
      try {
        var response = await apiClient.get(`/leads-managment/list-contacts`);
        this.selectcontacts = response.data.data.data;
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
    // get Accounts
    async getAllAccounts() {
      try {
        var response = await apiClient.get(`/leads-managment/accounts`);
        this.selectaccounts = response.data.data.data;
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
    //get projects
    async getAllprojects() {
      try {
        var response = await apiClient.get(`/leads-managment/projects`);
        this.selectprojects = response.data.data.data;
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
    //get Deal
    async getAllDeals() {
      try {
        var response = await apiClient.get(`/leads-managment/deals`);
        this.selectDeals = response.data.data.data;
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
    //get tasks
    async getAllTasks() {
      try {
        var response = await apiClient.get(`/leads-managment/tasks`);
        this.selectTasks = response.data.data.data;
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
  },
  mounted() {
    if (this.dialog) {
      this.initializeFlatPickr();
    }
  },
  created() {
    if (this.contacts.length > 0) {
      this.formData.contacts = this.contacts;
    }
    if (this.accounts.length > 0) {
      this.formData.accounts = this.accounts;
    }
  },
};
</script>
